// Классы и инлайн-стили оформления элементов дизайн-блока — 1:1 с
// ref DesignBlockEditor.tsx (textAlignClass/paddingClass/backdropStyles/textColor/highlight*).
import type { CSSProperties } from 'react'
import { blockTheme } from '@teach/ui'
import type {
  DesignBackdrop,
  DesignElementType,
  DesignFontWeight,
  DesignPadding,
  DesignTextAlign,
  DesignTextSize,
} from '@/types/blocks'

const db = blockTheme.designBlock

/** Типы, которые расставляют отступы САМИ (у бейджа это `gap` между пилюлями):
 *  общая обёртка канваса обязана в этом случае промолчать, иначе элемент получает
 *  двойной паддинг. Данные, а не `if` в обёртке: в рефе таких типов было два, и
 *  список заведомо будет расти — знание живёт рядом с `paddingClass`. */
export const SELF_PADDED: ReadonlySet<DesignElementType> = new Set<DesignElementType>(['badge'])

export const alignClass = (a: DesignTextAlign): string =>
  ({ left: 'text-left', center: 'text-center', right: 'text-right', justify: 'text-justify' })[a]

/** Отступ элемента — только по вертикали, как в мобилке: все элементы стоят
 *  ровной колонкой во всю ширину поля экрана (20), а боковой воздух у текста
 *  даёт линия текста (`TEXT_LINE_PADDING_X`). Боковой отступ элемента добавлял
 *  ещё 16 — превью переносило строки иначе, чем телефон. */
export const paddingClass = (p: DesignPadding): string =>
  ({ none: 'p-0', small: 'py-1', medium: 'py-2', large: 'py-4' })[p]

/** Внутреннее боковое поле подложки текста — оно же ЛИНИЯ ТЕКСТА: с неё
 *  начинаются заголовок, текст без подложки и текст в подложке; за неё
 *  выступает только сама подложка (и рамка таблицы). Одна константа на всех,
 *  чтобы при смене поля подложки заголовок сдвигался вместе с ним. */
export const TEXT_LINE_PADDING_X = 16

/** Средний шаг = кегль макета (110 и 70 на артборде 1654 → делитель 4.24).
 *  Остальные шаги пересчитаны вокруг него. Литералы целиком: Tailwind читает
 *  исходники как текст и склеенный в рантайме класс в сборку не попадёт. */
export const headingSizeClass = (s: DesignTextSize): string =>
  ({
    small: 'text-[20px]',
    medium: 'text-[26px]',
    large: 'text-[32px]',
    xlarge: 'text-[38px]',
  })[s]

export const textSizeClass = (s: DesignTextSize): string =>
  ({
    small: 'text-[13px]',
    medium: 'text-[16.5px]',
    large: 'text-[20px]',
    xlarge: 'text-[24px]',
  })[s]

export const fontWeightClass = (w: DesignFontWeight): string =>
  ({
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  })[w]

/** Подложка «blur» размытия больше не делает: в эталонной дизайн-системе это
 *  ПРОЗРАЧНАЯ подложка — рамка без заливки. Значение оставлено ради данных
 *  (менять enum значит ломать контракт с беком), имя историческое. */
export const backdropStyle = (b: DesignBackdrop, rounded: boolean): CSSProperties => {
  if (b === 'none') return {}

  const radius = rounded ? (b === 'blur' ? db.backdropTransparentRadius : db.backdropRadius) : '0'
  const common = { padding: `12px ${TEXT_LINE_PADDING_X}px`, borderRadius: radius }

  // Рамку прозрачной подложки рисует `TransparentFrame` (штрих с угасанием у
  // двух углов) — CSS-бордер бывает только сплошным. Здесь — внутренний блик.
  if (b === 'blur') return { ...common, boxShadow: db.backdropTransparentHighlight }
  const background = {
    light: db.backdropLight,
    dark: db.backdropDark,
    primary: db.backdropPrimary,
  }[b]
  // Объём есть только у акцентной подложки: внешняя тень плюс такая же внутренняя.
  return b === 'primary'
    ? { ...common, background, boxShadow: `${db.backdropShadow}, inset ${db.backdropShadow}` }
    : { ...common, background }
}

/** Коробка текстового элемента на холсте: подложка, а без неё — только боковой
 *  отступ до линии текста (у `backdropStyle('none')` его нет: пикер подложек
 *  рисует ту же функцию на крошечных плашках). */
export const textBoxStyle = (b: DesignBackdrop, rounded: boolean): CSSProperties =>
  b === 'none' ? { paddingInline: TEXT_LINE_PADDING_X } : backdropStyle(b, rounded)

export const backdropTextColor = (b: DesignBackdrop): string =>
  ({
    none: db.text,
    light: db.backdropLightText,
    dark: db.backdropDarkText,
    primary: db.backdropPrimaryText,
    blur: db.backdropTransparentText,
  })[b]

/** У какой подложки нет собственной заливки — выведено из фактического
 *  результата `backdropStyle`, а не отдельным списком. Один источник истины:
 *  если появится шестая беззаливочная подложка, поправят только резолвер
 *  `backdropStyle`, а этот предикат подхватит новую подложку сам собой —
 *  вручную дублируемый список про такую подложку рано или поздно забудут
 *  обновить, и ни один тест этого не поймает. */
export const backdropHasFill = (b: DesignBackdrop): boolean =>
  'background' in backdropStyle(b, false)

export interface HighlightTokens {
  marker: string
  markerText: string
  /** Один акцент на подчёркивание, волнистую и «жирность+цвет» — в макете это
   *  один и тот же цвет, поэтому и токен один. */
  accent: string
}

export const backdropHighlight = (b: DesignBackdrop): HighlightTokens => {
  const markerText = db.markerText
  if (b === 'dark') return { marker: db.markerOnDark, markerText, accent: db.accentOnDark }
  if (b === 'primary') return { marker: db.markerOnPrimary, markerText, accent: db.accentOnPrimary }
  if (b === 'light') return { marker: db.markerOnLight, markerText, accent: db.accentOnLight }
  return { marker: db.markerOnPlain, markerText, accent: db.accentOnLight }
}

// CSS выделений (маркер/подчёркивание/волна/жирный) — в designHighlightCss.ts.
export { highlightScopeStyles } from './designHighlightCss'
